/**
 * Hook for managing batch video export (all slides at once) + final assembly.
 *
 * Phases: idle → submitting → exporting → assembling → completed → failed
 * A program (one media clock, docs/program-mode.md) renders whole in one job:
 *         idle → submitting → rendering → completed → failed
 *
 * Key features:
 * - Auto-triggers assembly after all per-slide exports complete
 * - **Persists state in DB** — restores on page reload via GET /video-export/state,
 *   and re-reads it when the network comes back or the tab is shown again
 * - Shows download button permanently once assembly completes
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { videoExportAPI } from '../services/api';
import { announceLearnTrigger } from '../stores/embeddedIvyChatStore';

export interface SlideExportStatus {
  outlineSlideId: string;
  slideIdentifier: string;
  jobId: string;
  skipped: boolean;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  currentOperation?: string;
  error?: string;
}

export type ExportAllPhase = 'idle' | 'submitting' | 'exporting' | 'assembling' | 'rendering' | 'completed' | 'failed' | 'cancelled';

export interface ExportAllState {
  phase: ExportAllPhase;
  slides: SlideExportStatus[];
  overallProgress: number;
  assemblyJobId?: string;
  /** Parent ExportJob — used for aggregate status polling + cancel. */
  exportJobId?: string;
  /** True while we're loading persisted state from the server */
  isRestoring: boolean;
  /** True when the assembled video is outdated (slides changed since last assembly) */
  isStale: boolean;
  /** List of slide IDs that have been updated since the last assembly */
  staleSlides: string[];
  /** What a program render is doing now, as the job reports it. */
  currentOperation?: string;
  /** The job's reason, when an export failed. */
  error?: string;
}

export interface ExportToast {
  level: 'success' | 'error' | 'info';
  message: string;
}

/** The toast for a change of phase, or null. A state restored on page load says nothing. */
export function exportToastFor(prev: ExportAllPhase, next: ExportAllPhase): ExportToast | null {
  const wasRunning = prev === 'submitting' || prev === 'exporting' || prev === 'assembling' || prev === 'rendering';
  if (next === 'completed' && wasRunning) {
    return { level: 'success', message: 'Video export complete! Click "Download MP4" to save.' };
  }
  if (prev === 'exporting' && next === 'assembling') {
    return { level: 'info', message: 'Slides exported, combining into final video...' };
  }
  if (next !== 'failed') return null;
  if (prev === 'exporting') return { level: 'error', message: 'Some slides failed to export' };
  if (prev === 'assembling') return { level: 'error', message: 'Video assembly failed' };
  if (prev === 'rendering') return { level: 'error', message: 'Video export failed' };
  return null;
}

const POLL_INTERVAL = 3000;
const READ_RETRY_MS = 15000;

export function useVideoExportAll(conversationId?: string) {
  const [state, setState] = useState<ExportAllState>({
    phase: 'idle',
    slides: [],
    overallProgress: 0,
    isRestoring: !!conversationId,  // Assume restoring until proven otherwise
    isStale: false,
    staleSlides: [],
  });
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const slidesRef = useRef<SlideExportStatus[]>([]);
  const conversationIdRef = useRef<string>(conversationId || '');
  const assemblyJobIdRef = useRef<string | undefined>(undefined);
  const exportJobIdRef = useRef<string | undefined>(undefined);
  // Watermark/scale options from the most recent startExportAll. startAssembly()
  // MUST forward these to /assemble: the server bakes showWatermark + outputScale
  // into the assembly cache hash, so omitting them makes a no-watermark (or 4K)
  // request collide with — and silently return — a previously cached watermarked
  // assembly. That was the "Remove watermark does nothing" bug.
  const exportOptionsRef = useRef<{ removeWatermark?: boolean; outputScale?: '1080p' | '4k' }>({});
  const pollErrorCountRef = useRef<number>(0);
  const MAX_CONSECUTIVE_ERRORS = 5;
  // Each read of the server's state takes a number; a newer read or a new export
  // makes an older read's answer stale, so it never overwrites fresher state.
  const syncGenRef = useRef(0);
  const syncFromServerRef = useRef<(convId: string) => Promise<void>>(async () => undefined);
  // Consecutive failed reads of the server's state; while an export runs, each one schedules another read.
  const [readFailures, setReadFailures] = useState(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ====================================================================
  // Progress calculation
  // ====================================================================

  const computeOverallProgress = (slides: SlideExportStatus[], assemblyProgress?: number): number => {
    if (slides.length === 0) return assemblyProgress !== undefined ? 80 + Math.round(assemblyProgress * 0.2 / 100) : 0;
    const slideTotal = slides.reduce((sum, s) => sum + s.progress, 0);
    const slideProgress = Math.round(slideTotal / slides.length);
    const exportPortion = Math.round(slideProgress * 0.8);

    if (assemblyProgress !== undefined) {
      return exportPortion + Math.round(assemblyProgress * 0.2 / 100);
    }
    return exportPortion;
  };

  // ====================================================================
  // Assembly (or program render) polling
  // ====================================================================

  const pollAssemblyJob = useCallback(async () => {
    const assemblyJobId = assemblyJobIdRef.current;
    if (!assemblyJobId) return;

    try {
      const data = await videoExportAPI.getStatus(assemblyJobId);
      pollErrorCountRef.current = 0; // Reset on success

      const status = data.status;
      const percentage = data.progress?.percentage ?? 0;

      if (status === 'COMPLETED') {
        stopPolling();
        if (conversationIdRef.current) announceLearnTrigger(conversationIdRef.current);
        setState(prev => ({
          ...prev,
          phase: 'completed',
          overallProgress: 100,
          assemblyJobId,
          currentOperation: undefined,
          isStale: false,
          staleSlides: [],
        }));
      } else if (status === 'FAILED') {
        stopPolling();
        setState(prev => ({
          ...prev,
          phase: 'failed',
          assemblyJobId,
          error: data.error,
        }));
      } else if (status === 'CANCELLED') {
        stopPolling();
        setState(prev => ({ ...prev, phase: 'cancelled' }));
      } else if (data.metadata?.type === 'program') {
        // A program is the whole export in one job: its percentage is the overall one.
        setState(prev => ({
          ...prev,
          phase: 'rendering',
          overallProgress: percentage,
          currentOperation: data.progress?.currentOperation,
        }));
      } else {
        setState(prev => ({
          ...prev,
          overallProgress: 80 + Math.round(percentage * 0.2),
        }));
      }
    } catch (err) {
      pollErrorCountRef.current += 1;
      console.warn(`[export] Assembly poll error (${pollErrorCountRef.current}/${MAX_CONSECUTIVE_ERRORS}):`, err);

      // After too many consecutive errors, fall back to a full state refresh
      if (pollErrorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
        console.warn('[export] Too many poll errors — falling back to full state refresh');
        stopPolling();
        pollErrorCountRef.current = 0;
        if (conversationIdRef.current) await syncFromServerRef.current(conversationIdRef.current);
      }
    }
  }, [stopPolling]);

  const pollAssemblySoon = useCallback(() => {
    pollTimerRef.current = setInterval(pollAssemblyJob, POLL_INTERVAL);
    setTimeout(pollAssemblyJob, 1000);
  }, [pollAssemblyJob]);

  // ====================================================================
  // Start assembly
  // ====================================================================

  const startAssembly = useCallback(async () => {
    const convId = conversationIdRef.current;
    if (!convId) return;

    setState(prev => ({
      ...prev,
      phase: 'assembling',
      overallProgress: 80,
    }));

    try {
      // Forward the captured watermark/scale options so the assembly hash matches
      // what the slides were rendered for (otherwise we get the cached watermarked mp4).
      const response = await videoExportAPI.assemble(convId, exportOptionsRef.current);
      if (!response.success || !response.jobId) {
        setState(prev => ({ ...prev, phase: 'failed' }));
        return;
      }

      assemblyJobIdRef.current = response.jobId;
      setState(prev => ({
        ...prev,
        assemblyJobId: response.jobId,
      }));

      pollAssemblySoon();
    } catch (err) {
      console.error('Failed to start assembly:', err);
      setState(prev => ({ ...prev, phase: 'failed' }));
    }
  }, [pollAssemblySoon]);

  // ====================================================================
  // Slide export polling
  // ====================================================================

  const pollActiveJobs = useCallback(async () => {
    const current = slidesRef.current;
    const activeSlides = current.filter(
      s => !s.skipped && s.status !== 'COMPLETED' && s.status !== 'FAILED'
    );

    if (activeSlides.length === 0) {
      stopPolling();
      const allFailed = current.filter(s => !s.skipped).every(s => s.status === 'FAILED');
      if (allFailed) {
        setState(prev => ({
          ...prev,
          phase: 'failed',
          slides: [...current],
          overallProgress: computeOverallProgress(current),
        }));
      } else {
        // All slide exports done — auto-trigger assembly
        setState(prev => ({
          ...prev,
          slides: [...current],
          overallProgress: computeOverallProgress(current),
        }));
        startAssembly();
      }
      return;
    }

    const updates = await Promise.allSettled(
      activeSlides.map(s => videoExportAPI.getStatus(s.jobId))
    );

    // Track consecutive all-failed polls
    const allRejected = updates.every(r => r.status === 'rejected');
    if (allRejected) {
      pollErrorCountRef.current += 1;
      console.warn(`[export] All slide polls failed (${pollErrorCountRef.current}/${MAX_CONSECUTIVE_ERRORS})`);
      if (pollErrorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
        console.warn('[export] Too many slide poll errors — falling back to full state refresh');
        stopPolling();
        pollErrorCountRef.current = 0;
        if (conversationIdRef.current) await syncFromServerRef.current(conversationIdRef.current);
      }
      return;
    }
    pollErrorCountRef.current = 0;

    let changed = false;
    updates.forEach((result, i) => {
      if (result.status !== 'fulfilled') return;
      const data = result.value;
      const slide = activeSlides[i];
      const idx = current.findIndex(s => s.jobId === slide.jobId);
      if (idx === -1) return;

      const newStatus = data.status as SlideExportStatus['status'];
      const newProgress = data.progress?.percentage ?? 0;

      if (current[idx].status !== newStatus || current[idx].progress !== newProgress) {
        current[idx] = {
          ...current[idx],
          status: newStatus,
          progress: newProgress,
          currentOperation: data.progress?.currentOperation,
          error: data.error,
        };
        changed = true;
      }
    });

    if (changed) {
      slidesRef.current = [...current];
      const allDone = current.every(
        s => s.skipped || s.status === 'COMPLETED' || s.status === 'FAILED'
      );

      if (allDone) {
        stopPolling();
        const allFailed = current.filter(s => !s.skipped).every(s => s.status === 'FAILED');
        if (allFailed) {
          setState(prev => ({
            ...prev,
            phase: 'failed',
            slides: [...current],
            overallProgress: computeOverallProgress(current),
          }));
        } else {
          setState(prev => ({
            ...prev,
            slides: [...current],
            overallProgress: computeOverallProgress(current),
          }));
          startAssembly();
        }
      } else {
        setState(prev => ({
          ...prev,
          phase: 'exporting',
          slides: [...current],
          overallProgress: computeOverallProgress(current),
        }));
      }
    }
  }, [stopPolling, startAssembly]);

  // ====================================================================
  // Read the export's state from the server (page load, network back, tab shown)
  // ====================================================================

  const syncFromServer = useCallback(async (convId: string) => {
    const gen = ++syncGenRef.current;
    const superseded = () => gen !== syncGenRef.current;
    stopPolling();
    setState(prev => ({ ...prev, isRestoring: true }));

    try {
      const data = await videoExportAPI.getState(convId);
      if (superseded()) return;
      setReadFailures(0);

      // Restore the parent ExportJob id BEFORE branching into per-phase
      // setState calls. Without this, the canCancel gate stays false on a
      // tab reload because exportJobIdRef defaults to undefined — the user
      // sees a running export with no Cancel button.
      if (data.activeExportJobId) {
        exportJobIdRef.current = data.activeExportJobId;
      }

      const toSlides = (fallbackProgress: number): SlideExportStatus[] => data.slideExports.map(s => ({
        outlineSlideId: s.outlineSlideId,
        slideIdentifier: s.slideIdentifier,
        jobId: s.jobId,
        skipped: false,
        status: s.status as SlideExportStatus['status'],
        progress: s.progress?.percentage ?? fallbackProgress,
        error: s.error || undefined,
      }));

      // Priority 1: Active slide exports (PENDING/PROCESSING) always take precedence.
      // This handles the case where user re-exports while an old assembly exists:
      // without this check, the old COMPLETED assembly would short-circuit and
      // we'd never resume polling the new active exports.
      const hasActiveExports = data.slideExports.some(
        s => s.status === 'PENDING' || s.status === 'PROCESSING'
      );

      if (hasActiveExports) {
        const slides = toSlides(0);
        slidesRef.current = slides;

        setState({
          phase: 'exporting',
          slides,
          overallProgress: computeOverallProgress(slides),
          isRestoring: false,
          isStale: false,
          staleSlides: [],
          exportJobId: exportJobIdRef.current,
        });

        pollTimerRef.current = setInterval(pollActiveJobs, POLL_INTERVAL);
        setTimeout(pollActiveJobs, 1000);
        return;
      }

      // Priority 2: Assembly (or program render) in progress or completed
      if (data.assembly) {
        const assembly = data.assembly;
        assemblyJobIdRef.current = assembly.jobId;
        const slides = toSlides(100);
        slidesRef.current = slides;

        if (assembly.status === 'COMPLETED') {
          setState({
            phase: 'completed',
            slides,
            overallProgress: 100,
            assemblyJobId: assembly.jobId,
            isRestoring: false,
            isStale: assembly.isStale ?? false,
            staleSlides: assembly.staleSlides ?? [],
          });
          return;
        }

        if (assembly.status === 'PROCESSING' || assembly.status === 'PENDING') {
          // GET /state does not say whether this job is a program render or a slide
          // assembly; the job itself does (metadata.type).
          const job = await videoExportAPI.getStatus(assembly.jobId).catch(() => null);
          if (superseded()) return;
          const program = job?.metadata?.type === 'program';

          setState({
            phase: program ? 'rendering' : 'assembling',
            slides,
            overallProgress: program
              ? job!.progress?.percentage ?? 0
              : 80 + Math.round((assembly.progress?.percentage ?? 0) * 0.2),
            currentOperation: program ? job!.progress?.currentOperation : undefined,
            assemblyJobId: assembly.jobId,
            isRestoring: false,
            isStale: false,
            staleSlides: [],
            exportJobId: exportJobIdRef.current,
          });

          pollAssemblySoon();
          return;
        }

        if (assembly.status === 'FAILED') {
          setState({
            phase: 'failed',
            slides,
            overallProgress: 80,
            assemblyJobId: assembly.jobId,
            isRestoring: false,
            isStale: false,
            staleSlides: [],
            error: assembly.error || undefined,
          });
          return;
        }
      }

      // Priority 3: All slides done but no assembly — auto-trigger
      if (data.slideExports.length > 0) {
        const allSlidesDone = data.slideExports.every(
          s => s.status === 'COMPLETED' || s.status === 'FAILED'
        );
        const allFailed = data.slideExports.every(s => s.status === 'FAILED');

        if (allSlidesDone && !allFailed) {
          const slides = toSlides(100);
          slidesRef.current = slides;

          setState({
            phase: 'exporting',
            slides,
            overallProgress: 80,
            isRestoring: false,
            isStale: false,
            staleSlides: [],
          });

          startAssembly();
          return;
        }
      }

      // Nothing to restore
      setState(prev => ({ ...prev, isRestoring: false }));
    } catch (err) {
      if (!superseded()) {
        console.error('Failed to restore export state:', err);
        setState(prev => ({ ...prev, isRestoring: false }));
        setReadFailures(n => n + 1);
      }
    }
  }, [stopPolling, pollActiveJobs, pollAssemblySoon, startAssembly]);

  syncFromServerRef.current = syncFromServer;

  useEffect(() => {
    if (!conversationId) return;
    conversationIdRef.current = conversationId;
    void syncFromServer(conversationId);
    const generation = syncGenRef;
    return () => {
      generation.current++;
    };
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A laptop that slept, or a network that dropped, can leave polling stopped or
  // the shown progress stale: read the server's state again when either comes back.
  const isRunning = state.phase === 'exporting' || state.phase === 'assembling' || state.phase === 'rendering';
  useEffect(() => {
    if (!isRunning) return;
    const resync = () => {
      if (conversationIdRef.current) void syncFromServer(conversationIdRef.current);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') resync();
    };
    window.addEventListener('online', resync);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('online', resync);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isRunning, syncFromServer]);

  // Server errors with the network up fire neither event: keep reading slowly until it answers.
  useEffect(() => {
    if (!isRunning || readFailures === 0) return;
    const timer = setTimeout(() => {
      if (conversationIdRef.current) void syncFromServer(conversationIdRef.current);
    }, READ_RETRY_MS);
    return () => clearTimeout(timer);
  }, [isRunning, readFailures, syncFromServer]);

  // ====================================================================
  // Start export (user-initiated)
  // ====================================================================

  const startExportAll = useCallback(async (
    convId: string,
    options?: { force?: boolean; removeWatermark?: boolean; outputScale?: '1080p' | '4k' }
  ) => {
    stopPolling();
    syncGenRef.current++;
    setReadFailures(0);
    conversationIdRef.current = convId;
    assemblyJobIdRef.current = undefined;
    exportJobIdRef.current = undefined;
    // Capture for the assembly step — including the all-skipped (cached slides)
    // path below, which calls startAssembly() directly without re-rendering.
    exportOptionsRef.current = {
      removeWatermark: options?.removeWatermark,
      outputScale: options?.outputScale,
    };

    setState({ phase: 'submitting', slides: [], overallProgress: 0, isRestoring: false, isStale: false, staleSlides: [] });

    try {
      const response = await videoExportAPI.startAll(convId, options);

      // Capture the parent ExportJob so cancel() can cascade later.
      if (response.exportJobId) {
        exportJobIdRef.current = response.exportJobId;
      }

      // A program renders whole in one job: there are no slides to list or assemble.
      if (response.program) {
        const { status, jobId } = response.program;
        assemblyJobIdRef.current = jobId;
        if (status === 'cached') {
          announceLearnTrigger(convId);
          setState({ phase: 'completed', slides: [], overallProgress: 100, assemblyJobId: jobId, isRestoring: false, isStale: false, staleSlides: [], exportJobId: exportJobIdRef.current });
          return;
        }
        setState({ phase: 'rendering', slides: [], overallProgress: 0, currentOperation: 'Queued', assemblyJobId: jobId, isRestoring: false, isStale: false, staleSlides: [], exportJobId: exportJobIdRef.current });
        pollAssemblySoon();
        return;
      }

      if (!response.success || !response.slides || response.slides.length === 0) {
        setState({ phase: 'failed', slides: [], overallProgress: 0, isRestoring: false, isStale: false, staleSlides: [], exportJobId: response.exportJobId });
        return;
      }

      const slides: SlideExportStatus[] = response.slides.map(s => ({
        outlineSlideId: s.outlineSlideId,
        slideIdentifier: s.slideIdentifier,
        jobId: s.jobId,
        skipped: s.skipped,
        status: s.skipped ? 'COMPLETED' : 'PENDING',
        progress: s.skipped ? 100 : 0,
        currentOperation: s.skipped ? 'Already exported' : 'Queued...',
      }));

      slidesRef.current = slides;

      const allSkipped = slides.every(s => s.skipped);
      if (allSkipped) {
        setState({ phase: 'exporting', slides, overallProgress: 80, isRestoring: false, isStale: false, staleSlides: [], exportJobId: exportJobIdRef.current });
        startAssembly();
        return;
      }

      setState({
        phase: 'exporting',
        slides,
        overallProgress: computeOverallProgress(slides),
        isRestoring: false,
        isStale: false,
        staleSlides: [],
        exportJobId: exportJobIdRef.current,
      });

      pollTimerRef.current = setInterval(pollActiveJobs, POLL_INTERVAL);
      setTimeout(pollActiveJobs, 1000);
    } catch (err) {
      setState({ phase: 'failed', slides: [], overallProgress: 0, isRestoring: false, isStale: false, staleSlides: [], exportJobId: exportJobIdRef.current });
      throw err;
    }
  }, [stopPolling, pollActiveJobs, pollAssemblySoon, startAssembly]);

  // ====================================================================
  // Cancel export (cascade to workers)
  // ====================================================================

  const cancel = useCallback(async () => {
    const exportJobId = exportJobIdRef.current;
    if (!exportJobId) {
      // Nothing to cancel — just reset local state.
      stopPolling();
      setState(prev => ({ ...prev, phase: 'cancelled' }));
      return;
    }
    try {
      await videoExportAPI.cancelExportJob(exportJobId);
      stopPolling();
      setState(prev => ({ ...prev, phase: 'cancelled' }));
    } catch (err) {
      console.error('Failed to cancel export:', err);
      // Don't flip phase on failure — user can retry the cancel.
      throw err;
    }
  }, [stopPolling]);

  // ====================================================================
  // Reset
  // ====================================================================

  const reset = useCallback(() => {
    stopPolling();
    slidesRef.current = [];
    conversationIdRef.current = conversationId || '';
    assemblyJobIdRef.current = undefined;
    setState({ phase: 'idle', slides: [], overallProgress: 0, isRestoring: false, isStale: false, staleSlides: [] });
  }, [stopPolling, conversationId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const isExporting = state.phase === 'submitting' || isRunning;

  return {
    ...state,
    startExportAll,
    cancel,
    reset,
    isExporting,
    /** True when cancel is available (something is in flight AND we have a parent job id). */
    canCancel: isExporting && !!exportJobIdRef.current,
  };
}
